import React, { useState, useEffect, useMemo } from 'react';
import { ThumbsUp, MessageCircle, Trash2, MapPin } from 'lucide-react';
import api from '../api';
import '../styles/CrimeReport.css';
import './Reports.css';

const Reports = ({ user }) => {
  const [activeReports, setActiveReports] = useState([]);
  const [deletedReports, setDeletedReports] = useState([]);
  const [isLoadingActive, setIsLoadingActive] = useState(false);
  const [isLoadingDeleted, setIsLoadingDeleted] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'deleted'
  const [reportAddresses, setReportAddresses] = useState({});
  const [reportsConfig, setReportsConfig] = useState(null);
  
  // Filters
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'week', 'month', 'year'
  const [levelFilter, setLevelFilter] = useState('all'); // 'all', 'level_1', 'level_2', 'level_3'

  // Fetch reports when component mounts
  useEffect(() => {
    if (user) {
      const userId = user.id || user._id || user.userId;
      if (userId) {
        fetchActiveReports(userId);
        fetchDeletedReports(userId);
      }
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/reports/config');
        if (!cancelled) setReportsConfig(res.data || null);
      } catch (e) {
        if (!cancelled) setReportsConfig(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const severityOptions = useMemo(() => {
    const rows = reportsConfig?.severityTypes;
    if (Array.isArray(rows) && rows.length) {
      return rows
        .filter(r => r && r.type)
        .slice()
        .sort((a, b) => (Number(a.severityRank) || 0) - (Number(b.severityRank) || 0));
    }
    return [
      { type: 'level_1', severityRank: 1, label: 'Low Severity' },
      { type: 'level_2', severityRank: 2, label: 'Medium Severity' },
      { type: 'level_3', severityRank: 3, label: 'High Severity' }
    ];
  }, [reportsConfig]);

  // Fetch active user reports
  const fetchActiveReports = async (userId) => {
    setIsLoadingActive(true);
    setError(null);
    try {
      const response = await api.get(`/api/reports/user/${userId}?status=active`);
      setActiveReports(response.data);
    } catch (err) {
      console.error('Error fetching active reports:', err);
      setError('Unable to load your active reports. Please try again later.');
    } finally {
      setIsLoadingActive(false);
    }
  };

  // Fetch deleted user reports
  const fetchDeletedReports = async (userId) => {
    setIsLoadingDeleted(true);
    try {
      const response = await api.get(`/api/reports/user/${userId}?status=deleted`);
      setDeletedReports(response.data);
    } catch (err) {
      console.error('Error fetching deleted reports:', err);
    } finally {
      setIsLoadingDeleted(false);
    }
  };

  // Delete a report
  const deleteReport = async (reportId) => {
    if (window.confirm('Are you sure you want to delete this report? It will be moved to your deleted reports section.')) {
      try {
        await api.delete(`/api/reports/${reportId}`);
        const deletedReport = activeReports.find(report => report.id === reportId);
        if (deletedReport) {
          setActiveReports(activeReports.filter(report => report.id !== reportId));
          setDeletedReports([{...deletedReport, isDeleted: true, deletedAt: new Date().toISOString()}, ...deletedReports]);
        }
        alert('Report deleted successfully');
      } catch (err) {
        console.error('Error deleting report:', err);
        alert('Failed to delete the report. Please try again.');
      }
    }
  };

  // Permanently delete a report
  const permanentlyDeleteReport = async (reportId) => {
    if (window.confirm('Are you sure you want to PERMANENTLY delete this report? This action cannot be undone.')) {
      try {
        await api.delete(`/api/reports/hard/${reportId}`);
        setDeletedReports(deletedReports.filter(report => report.id !== reportId));
        alert('Report permanently deleted');
      } catch (err) {
        console.error('Error permanently deleting report:', err);
        alert('Failed to permanently delete the report. Please try again.');
      }
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Load addresses from reports
  useEffect(() => {
    const newAddresses = {};
    [...activeReports, ...deletedReports].forEach(report => {
      if (report.address) {
        newAddresses[report.id] = report.address;
      } else if (report.position && !reportAddresses[report.id]) {
        newAddresses[report.id] = `${report.position.lat.toFixed(4)}, ${report.position.lng.toFixed(4)}`;
      }
    });
    if (Object.keys(newAddresses).length > 0) {
      setReportAddresses(prev => ({ ...prev, ...newAddresses }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReports, deletedReports]);

  // Get type label
  const getTypeLabel = (type) => {
    const hit = severityOptions.find(s => s.type === type);
    if (hit && hit.label) return hit.label;
    const typeLabels = {
      'level1': 'Low Severity',
      'level2': 'Medium Severity',
      'level3': 'High Severity',
      'level_1': 'Low Severity',
      'level_2': 'Medium Severity',
      'level_3': 'High Severity'
    };
    return typeLabels[type] || type;
  };

  // Filter reports based on selected filters
  const filterReports = (reports) => {
    let filtered = [...reports];

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const cutoffDate = new Date();
      
      switch (dateFilter) {
        case 'week':
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        case 'year':
          cutoffDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          break;
      }
      
      filtered = filtered.filter(report => 
        new Date(report.timestamp) >= cutoffDate
      );
    }

    // Level filter
    if (levelFilter !== 'all') {
      filtered = filtered.filter(report => {
        // Check both 'type' and 'severity' fields (reports might use either)
        const reportLevel = report.type || report.severity || report.level;
        return reportLevel === levelFilter;
      });
    }

    return filtered;
  };

  // Get filtered reports
  const filteredActiveReports = filterReports(activeReports);
  const filteredDeletedReports = filterReports(deletedReports);

  return (
    <div className="reports-page">
      <div className="reports-header">
        <h2>My Reports</h2>
        <p className="reports-subtitle">Manage and view your submitted safety reports</p>
      </div>

      <div className="reports-tabs">
        <button 
          className={`reports-tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          <span>Active Reports</span>
          {filteredActiveReports.length > 0 && (
            <span className="tab-badge">
              {filteredActiveReports.length}
              {activeReports.length !== filteredActiveReports.length && ` / ${activeReports.length}`}
            </span>
          )}
        </button>
        <button 
          className={`reports-tab ${activeTab === 'deleted' ? 'active' : ''}`}
          onClick={() => setActiveTab('deleted')}
        >
          <span>Deleted Reports</span>
          {filteredDeletedReports.length > 0 && (
            <span className="tab-badge">
              {filteredDeletedReports.length}
              {deletedReports.length !== filteredDeletedReports.length && ` / ${deletedReports.length}`}
            </span>
          )}
        </button>
      </div>

      {/* Filters */}
      <div className="reports-filters">
        <div className="filter-group">
          <label>Date Range:</label>
          <select 
            value={dateFilter} 
            onChange={(e) => setDateFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Time</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last Month</option>
            <option value="year">Last Year</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Severity Level:</label>
          <select 
            value={levelFilter} 
            onChange={(e) => setLevelFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Levels</option>
            {severityOptions.map(opt => (
              <option key={opt.type} value={opt.type}>
                {opt.label || opt.type}
              </option>
            ))}
          </select>
        </div>

        {(dateFilter !== 'all' || levelFilter !== 'all') && (
          <button 
            className="filter-clear"
            onClick={() => {
              setDateFilter('all');
              setLevelFilter('all');
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="reports-content">
        {/* Active Reports */}
        {activeTab === 'active' && (
          <div className="reports-list-container">
            {isLoadingActive ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading your reports...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <p>{error}</p>
              </div>
            ) : filteredActiveReports.length === 0 ? (
              <div className="empty-state">
                <MapPin size={48} strokeWidth={1.5} />
                <h3>{activeReports.length === 0 ? 'No Active Reports' : 'No Reports Match Filters'}</h3>
                <p>{activeReports.length === 0 
                  ? "You haven't submitted any reports yet. Start by reporting an incident on the map."
                  : "Try adjusting your filters to see more reports."}
                </p>
              </div>
            ) : (
              <div className="reports-grid">
                {filteredActiveReports.map((report) => (
                  <div key={report.id} className="report-card">
                    <div className="report-card-header">
                      <span className={`severity-badge ${report.type}`}>
                        {getTypeLabel(report.type)}
                      </span>
                      <span className="report-date">{formatDate(report.timestamp)}</span>
                    </div>
                    
                    <div className="report-card-body">
                      <p className="report-description">{report.description}</p>
                      <div className="report-location">
                        <MapPin size={14} strokeWidth={2} />
                        <span>{reportAddresses[report.id] || 'Loading address...'}</span>
                      </div>
                    </div>
                    
                    <div className="report-card-stats">
                      <div className="stat-item">
                        <ThumbsUp size={14} strokeWidth={2} />
                        <span>{report.upvoteCount || 0}</span>
                      </div>
                      <div className="stat-item">
                        <MessageCircle size={14} strokeWidth={2} />
                        <span>{report.comments?.length || 0}</span>
                      </div>
                    </div>
                    
                    <div className="report-card-footer">
                      <button 
                        className="delete-btn"
                        onClick={() => deleteReport(report.id)}
                      >
                        <Trash2 size={16} strokeWidth={2} />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Deleted Reports */}
        {activeTab === 'deleted' && (
          <div className="reports-list-container">
            {isLoadingDeleted ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading deleted reports...</p>
              </div>
            ) : filteredDeletedReports.length === 0 ? (
              <div className="empty-state">
                <Trash2 size={48} strokeWidth={1.5} />
                <h3>{deletedReports.length === 0 ? 'No Deleted Reports' : 'No Reports Match Filters'}</h3>
                <p>{deletedReports.length === 0
                  ? "You don't have any deleted reports."
                  : "Try adjusting your filters to see more reports."}
                </p>
              </div>
            ) : (
              <div className="reports-grid">
                {filteredDeletedReports.map((report) => (
                  <div key={report.id} className="report-card deleted">
                    <div className="report-card-header">
                      <span className={`severity-badge ${report.type}`}>
                        {getTypeLabel(report.type)}
                      </span>
                      <span className="report-date deleted">Deleted: {formatDate(report.deletedAt)}</span>
                    </div>
                    
                    <div className="report-card-body">
                      <p className="report-description">{report.description}</p>
                      <div className="report-location">
                        <MapPin size={14} strokeWidth={2} />
                        <span>{reportAddresses[report.id] || 'Loading address...'}</span>
                      </div>
                    </div>
                    
                    <div className="report-card-footer">
                      <button 
                        className="delete-btn permanent"
                        onClick={() => permanentlyDeleteReport(report.id)}
                      >
                        <Trash2 size={16} strokeWidth={2} />
                        Delete Permanently
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
